import { useCallback, useEffect, useState } from 'react';
import {
  HandCoins, Loader2, Wallet, Mail, Inbox, ScrollText, Save,
  AlertCircle, ShieldCheck, Package,
} from 'lucide-react';
import {
  refreshRmt, getRmtDeposits, getRmtLedger, updateRmtSellerConfig,
  setSellerAlz, getSellerItems, updateItemListing,
  type RmtMe, type RmtDeposit, type RmtLedgerEntry, type RmtItemListing,
} from '@/shared/services/rmtService';
import { API_URL } from '@/shared/services/authServices';
import { useToast } from '@/shared/contexts/ToastContext';

const fmt = (n: number) => (n ?? 0).toLocaleString('pt-BR');
const fmtDate = (s: string | null) => (s ? new Date(s).toLocaleString('pt-BR') : '—');

const PIX_TYPES: { value: string; label: string }[] = [
  { value: '', label: '— tipo —' },
  { value: 'cpf', label: 'CPF' },
  { value: 'cnpj', label: 'CNPJ' },
  { value: 'email', label: 'E-mail' },
  { value: 'phone', label: 'Telefone' },
  { value: 'random', label: 'Aleatória' },
];

const ENTRY_LABEL: Record<string, string> = {
  deposit: 'Depósito', sale: 'Venda', withdrawal: 'Retirada', adjustment: 'Ajuste',
};
const DOC_LABEL: Record<string, string> = {
  pending: 'Pendente', approved: 'Aprovada', rejected: 'Rejeitada',
};


// ----------------------------------------------------------------- item helpers

function ItemIcon({ itemId }: { itemId: number | null }) {
  const [ok, setOk] = useState(true);
  if (!itemId || !ok) {
    return (
      <div className="w-12 h-12 rounded bg-[#0d141b] border border-white/[0.06]
                      flex items-center justify-center shrink-0">
        <Package size={18} className="text-white/25" />
      </div>
    );
  }
  return (
    <div className="w-12 h-12 rounded bg-[#0d141b] border border-white/[0.06] overflow-hidden shrink-0">
      <img src={`${API_URL}/admin/item-icon/${itemId}.png?v=nobg2`} onError={() => setOk(false)}
           alt="" className="w-full h-full object-contain" loading="lazy" />
    </div>
  );
}

function itemBadges(it: RmtItemListing): { text: string; cls: string }[] {
  const b: { text: string; cls: string }[] = [];
  if (it.upgrade > 0) b.push({ text: `+${it.upgrade}`, cls: 'bg-amber-500/15 text-amber-400' });
  if (it.divine > 0) b.push({ text: `Divine +${it.divine}`, cls: 'bg-purple-500/15 text-purple-300' });
  if (it.extreme > 0) b.push({ text: `Extreme +${it.extreme}`, cls: 'bg-cyan-500/15 text-cyan-300' });
  if (it.slotCount > 0) b.push({ text: `${it.slotCount} slot${it.slotCount > 1 ? 's' : ''}`, cls: 'bg-white/10 text-white/70' });
  if (it.sealed) b.push({ text: 'Selado', cls: 'bg-white/10 text-white/60' });
  if (it.bound) b.push({ text: 'Vinculado', cls: 'bg-red-500/10 text-red-300' });
  return b;
}

function ItemListingCard({ it, onChanged }: { it: RmtItemListing; onChanged: () => void }) {
  const { notify } = useToast();
  const [price, setPrice] = useState(it.priceBrl);
  const [busy, setBusy] = useState(false);
  const sold = it.status === 'sold';

  const save = async (enabled: boolean) => {
    setBusy(true);
    try {
      await updateItemListing(it.listingId, price, enabled);
      notify('success', enabled ? 'Item à venda' : 'Item atualizado');
      onChanged();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro ao salvar');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-3 px-4 py-3 border-b border-white/[0.04] last:border-0">
      <ItemIcon itemId={it.baseItemId} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-white truncate">
          {it.itemName || `Item #${it.baseItemId ?? '?'}`}
        </p>
        <div className="flex flex-wrap gap-1 mt-1">
          {itemBadges(it).map((b, i) => (
            <span key={i} className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${b.cls}`}>{b.text}</span>
          ))}
          {it.enabled && !sold && (
            <span className="text-[9px] px-1.5 py-0.5 rounded font-bold bg-emerald-500/15 text-emerald-400">
              À VENDA
            </span>
          )}
        </div>
      </div>

      {sold ? (
        <span className="text-[10px] font-black uppercase px-2.5 py-1 rounded bg-white/10 text-white/50 shrink-0">
          Vendido
        </span>
      ) : it.blacklisted ? (
        <span className="text-[10px] font-black uppercase px-2.5 py-1 rounded bg-red-500/15 text-red-400 shrink-0"
              title="Item bloqueado pela administração">
          Bloqueado
        </span>
      ) : (
        <div className="flex items-center gap-2 shrink-0">
          <div className="relative">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[11px] text-white/40">R$</span>
            <input
              type="number" min={0} step="0.01" value={price}
              onChange={(e) => setPrice(Math.max(0, Number(e.target.value) || 0))}
              className="w-24 bg-[#0d141b] border border-white/[0.06] rounded-lg
                         pl-7 pr-2 py-1.5 text-sm text-white"
            />
          </div>
          <button
            onClick={() => save(true)}
            disabled={busy}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer transition
                        disabled:opacity-50 flex items-center gap-1
                        ${it.enabled ? 'bg-white/[0.06] text-white/80 hover:bg-white/[0.1]'
                                     : 'bg-amber-500 text-[#0d141b] hover:bg-amber-400'}`}
          >
            {busy && <Loader2 size={12} className="animate-spin" />}
            {it.enabled ? 'Atualizar' : 'Habilitar'}
          </button>
          {it.enabled && (
            <button
              onClick={() => save(false)}
              disabled={busy}
              className="px-2.5 py-1.5 rounded-lg text-xs bg-red-500/10 text-red-400
                         hover:bg-red-500/20 cursor-pointer disabled:opacity-50"
            >
              Tirar
            </button>
          )}
        </div>
      )}
    </div>
  );
}


// ----------------------------------------------------------------- page

export default function RmtPage() {
  const { notify } = useToast();
  const [me, setMe] = useState<RmtMe | null>(null);
  const [deposits, setDeposits] = useState<RmtDeposit[]>([]);
  const [ledger, setLedger] = useState<RmtLedgerEntry[]>([]);
  const [items, setItems] = useState<RmtItemListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // dados de vendedor
  const [sellerName, setSellerName] = useState('');
  const [pixKey, setPixKey] = useState('');
  const [pixKeyType, setPixKeyType] = useState('');
  const [saving, setSaving] = useState(false);

  // alz à venda
  const [availQty, setAvailQty] = useState(0);
  const [alzPrice, setAlzPrice] = useState(0);
  const [savingAlz, setSavingAlz] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const m = await refreshRmt();   // dispara o scan + devolve status atualizado
      setMe(m);
      setSellerName(m.sellerName || '');
      setPixKey(m.pixKey || '');
      setPixKeyType(m.pixKeyType || '');
      setAvailQty(m.availableAlz || 0);
      setAlzPrice(m.alzPriceBrl || 0);
      if (m.isSeller) {
        const [dep, led, its] = await Promise.all([
          getRmtDeposits(50), getRmtLedger(50), getSellerItems(),
        ]);
        setDeposits(dep);
        setLedger(led);
        setItems(its);
      }
    } catch (err: any) {
      setError(err?.detail || err?.message || 'Erro carregando RMT');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const reloadItems = useCallback(async () => {
    try {
      const [m, its] = await Promise.all([refreshRmt(), getSellerItems()]);
      setMe(m);
      setItems(its);
    } catch { /* silencioso */ }
  }, []);

  const saveConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const m = await updateRmtSellerConfig({
        sellerName, pixKey, pixKeyType: pixKeyType || undefined,
      });
      setMe(m);
      notify('success', 'Dados de vendedor salvos');
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const saveAlz = async () => {
    setSavingAlz(true);
    try {
      const m = await setSellerAlz(availQty, alzPrice);
      setMe(m);
      notify('success', 'Alz à venda atualizado');
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro ao salvar');
    } finally {
      setSavingAlz(false);
    }
  };

  if (loading) {
    return (
      <div className="py-20 flex items-center justify-center text-white/40">
        <Loader2 size={22} className="animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="surface-card rounded-xl p-5 flex items-start gap-3 border-red-500/30">
        <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
        <p className="text-sm text-red-400">{error}</p>
      </div>
    );
  }

  if (!me?.isSeller) {
    return (
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-6">Meu RMT</h1>
        <div className="surface-card rounded-xl p-8 text-center">
          <HandCoins size={32} className="text-white/30 mx-auto mb-3" />
          <p className="text-white font-semibold">Conta não habilitada como vendedor RMT</p>
          <p className="text-sm text-white/55 mt-1">
            O acesso de vendedor é concedido pela administração.
          </p>
        </div>
      </div>
    );
  }

  const suspended = me.status === 'suspended';

  return (
    <div>
      <h1 className="text-2xl md:text-3xl font-bold text-white mb-6 flex items-center gap-3">
        <HandCoins className="text-[#8fb9de]" size={26} />
        Meu RMT
      </h1>

      {suspended && (
        <div className="surface-card rounded-xl p-4 mb-6 flex items-start gap-3 border-red-500/30">
          <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
          <p className="text-sm text-red-400">
            Sua conta de vendedor está <b>suspensa</b>. Contate a administração.
          </p>
        </div>
      )}

      {me.throttled && !suspended && (
        <div className="surface-card rounded-xl p-4 mb-6 flex items-start gap-3 border-amber-500/30">
          <AlertCircle size={18} className="text-amber-400 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-400">
            Você atingiu {me.throttleReason || 'um limite de venda'}. Você não aparece
            na lista de compra até o limite renovar.
          </p>
        </div>
      )}

      {/* Alz à venda */}
      <div className="surface-card rounded-xl p-6 mb-6">
        <div className="flex items-center gap-2 mb-1">
          <Wallet size={18} className="text-[#8fb9de]" />
          <h2 className="text-lg font-bold text-white">Alz à venda</h2>
        </div>
        <p className="text-xs text-white/55 mb-4">
          Saldo depositado em escrow: <b className="text-[#8fb9de]">{fmt(me.balanceAlz)} alz</b>.
          Defina quanto desse saldo fica disponível pra compra e o preço por 100kk.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-white/65 mb-1.5">Alz disponível para venda</label>
            <input
              type="number" min={0} value={availQty}
              onChange={(e) => setAvailQty(Math.max(0, Number(e.target.value) || 0))}
              className="w-full px-4 py-2.5 rounded-lg text-sm bg-[#0d141b]
                         border border-white/[0.06] text-white"
            />
            <p className="text-[11px] text-white/40 mt-1">Máximo: {fmt(me.balanceAlz)} alz</p>
          </div>
          <div>
            <label className="block text-xs text-white/65 mb-1.5">Preço por 100kk (R$)</label>
            <input
              type="number" min={0} step="0.01" value={alzPrice}
              onChange={(e) => setAlzPrice(Math.max(0, Number(e.target.value) || 0))}
              className="w-full px-4 py-2.5 rounded-lg text-sm bg-[#0d141b]
                         border border-white/[0.06] text-white"
            />
          </div>
        </div>
        <button
          onClick={saveAlz}
          disabled={savingAlz}
          className="btn-primary px-6 py-2.5 rounded-lg text-sm font-semibold mt-4
                     flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer"
        >
          {savingAlz && <Loader2 size={14} className="animate-spin" />}
          Salvar alz à venda
        </button>
      </div>

      {/* Como depositar */}
      <div className="surface-card rounded-xl p-6 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Mail size={18} className="text-[#8fb9de]" />
          <h2 className="text-lg font-bold text-white">Como depositar</h2>
        </div>
        {me.cofreCharName ? (
          <>
            <p className="text-sm text-white/75">
              Envie alz ou um item pelo <b>correio do jogo</b> para o personagem:
            </p>
            <div className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-lg
                            bg-[#8fb9de]/10 border border-[#8fb9de]/25">
              <ShieldCheck size={15} className="text-[#8fb9de]" />
              <span className="text-base font-black text-[#8fb9de]">{me.cofreCharName}</span>
            </div>
            <ul className="text-xs text-white/55 mt-3 space-y-1 list-disc pl-4">
              <li>Qualquer personagem da sua conta pode enviar.</li>
              <li>O depósito é detectado automaticamente (em até ~1 min).</li>
              <li><b>Alz</b> cai no saldo em escrow; <b>itens</b> aparecem em "Meus itens" abaixo.</li>
              <li>Envie alz e item em correios <b>separados</b> — um de cada vez.</li>
              {me.minDepositAlz > 0 && (
                <li>Depósito mínimo de alz sugerido: <b>{fmt(me.minDepositAlz)} alz</b>.</li>
              )}
            </ul>
          </>
        ) : (
          <p className="text-sm text-amber-400">
            O sistema de depósito ainda não foi configurado pela administração.
          </p>
        )}
      </div>

      {/* Meus itens */}
      <div className="surface-card rounded-xl overflow-hidden mb-6">
        <div className="px-5 py-3 bg-[#1a2531] border-b border-white/[0.06] flex items-center gap-2">
          <Package size={14} className="text-[#8fb9de]" />
          <p className="text-xs font-black uppercase tracking-[0.15em] text-white/55">
            Meus itens ({items.length})
          </p>
        </div>
        {items.length === 0 ? (
          <div className="py-12 text-center text-white/40 text-sm">
            Nenhum item depositado. Envie um item pelo correio ao char-cofre.
          </div>
        ) : (
          items.map(it => (
            <ItemListingCard key={it.listingId} it={it} onChanged={reloadItems} />
          ))
        )}
      </div>

      {/* Dados de vendedor */}
      <div className="surface-card rounded-xl p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Save size={18} className="text-[#8fb9de]" />
            <h2 className="text-lg font-bold text-white">Dados de vendedor</h2>
          </div>
          <span className={`text-[10px] px-2 py-0.5 rounded font-bold
                            ${me.docStatus === 'approved' ? 'bg-emerald-500/15 text-emerald-400'
                              : me.docStatus === 'rejected' ? 'bg-red-500/15 text-red-400'
                              : 'bg-amber-500/15 text-amber-400'}`}>
            Doc: {DOC_LABEL[me.docStatus || 'pending']}
          </span>
        </div>
        <form onSubmit={saveConfig} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="block text-xs text-white/65 mb-1.5">Nome de vendedor</label>
            <input
              type="text" value={sellerName} onChange={(e) => setSellerName(e.target.value)}
              maxLength={64} placeholder="Nome exibido no marketplace"
              className="w-full px-4 py-2.5 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-white/65 mb-1.5">Tipo da chave PIX</label>
            <select
              value={pixKeyType} onChange={(e) => setPixKeyType(e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg text-sm bg-[#0d141b]
                         border border-white/[0.06] text-white"
            >
              {PIX_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-white/65 mb-1.5">Chave PIX</label>
            <input
              type="text" value={pixKey} onChange={(e) => setPixKey(e.target.value)}
              maxLength={140} placeholder="Sua chave PIX para recebimento"
              className="w-full px-4 py-2.5 rounded-lg text-sm"
            />
          </div>
          <div className="sm:col-span-2">
            <button
              type="submit" disabled={saving}
              className="btn-primary px-6 py-2.5 rounded-lg text-sm font-semibold
                         flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer"
            >
              {saving && <Loader2 size={14} className="animate-spin" />}
              Salvar dados
            </button>
          </div>
        </form>
      </div>

      {/* Depósitos */}
      <div className="surface-card rounded-xl overflow-hidden mb-6">
        <div className="px-5 py-3 bg-[#1a2531] border-b border-white/[0.06] flex items-center gap-2">
          <Inbox size={14} className="text-[#8fb9de]" />
          <p className="text-xs font-black uppercase tracking-[0.15em] text-white/55">
            Depósitos de alz recentes
          </p>
        </div>
        {deposits.length === 0 ? (
          <div className="py-12 text-center text-white/40 text-sm">Nenhum depósito ainda</div>
        ) : (
          deposits.map(d => (
            <div key={d.depositId}
                 className="flex items-center justify-between gap-4 px-5 py-3
                            border-b border-white/[0.04] last:border-0">
              <div className="min-w-0">
                <p className="text-sm font-bold text-white">+ {fmt(d.alzAmount)} alz</p>
                <p className="text-[11px] text-white/45 font-mono">
                  de {d.senderName || `char ${d.senderCharIdx}`} · {fmtDate(d.detectedAt)}
                </p>
              </div>
              <span className="text-[10px] text-white/40 font-mono shrink-0">mail #{d.mailId}</span>
            </div>
          ))
        )}
      </div>

      {/* Razão de movimentos */}
      <div className="surface-card rounded-xl overflow-hidden">
        <div className="px-5 py-3 bg-[#1a2531] border-b border-white/[0.06] flex items-center gap-2">
          <ScrollText size={14} className="text-[#8fb9de]" />
          <p className="text-xs font-black uppercase tracking-[0.15em] text-white/55">
            Movimentos do escrow
          </p>
        </div>
        {ledger.length === 0 ? (
          <div className="py-12 text-center text-white/40 text-sm">Nenhum movimento</div>
        ) : (
          ledger.map(l => (
            <div key={l.ledgerId}
                 className="flex items-center justify-between gap-4 px-5 py-3
                            border-b border-white/[0.04] last:border-0">
              <div className="min-w-0">
                <p className="text-sm font-medium text-white">
                  {ENTRY_LABEL[l.entryType] || l.entryType}
                  <span className={`ml-2 font-bold ${l.alzDelta >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {l.alzDelta >= 0 ? '+' : ''}{fmt(l.alzDelta)} alz
                  </span>
                </p>
                <p className="text-[11px] text-white/45 font-mono">{fmtDate(l.createdAt)}</p>
              </div>
              <span className="text-[11px] text-white/55 shrink-0">saldo: {fmt(l.balanceAfter)}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
